import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Snackbar, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckSmallIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import CSVImportDialog from '../imports/CSVImportDialog';
import CustomerDialog from '../customers/CustomerDialog';
import AutoFuturMigrationDialog from './AutoFuturMigrationDialog';
import ChecklistSlimBar from './ChecklistSlimBar';
import ChecklistStepRow from './ChecklistStepRow';
import GuideStepPanel from './GuideStepPanel';
import ImportStepPanel from './ImportStepPanel';
import PartsStepPanel from './PartsStepPanel';
import TerminalStepPanel from './TerminalStepPanel';
import { STEP_GUIDES } from './stepGuides';

import { can } from '../../utils/access';
const SLIM_THRESHOLD = 3;
// Steps whose CTA lands on an admin-only settings tab.
const ADMIN_ONLY_STEPS = new Set(['einvoice', 'suppliers', 'staff', 'booking']);

/**
 * Post-registration onboarding checklist shown above the dashboard grid (fixed chrome,
 * not a widget: see widget-dashboard/DashboardOnboarding.js).
 *
 * Steps auto-complete from real data (the backend decides), guide panels deep-link
 * into the right place in the app. Renders nothing when the feature is not enabled
 * for this organization, when the user has hidden it, or when everything is done.
 */
function OnboardingChecklistCard({ onCreateWorkOrder, onOpenInvoiceables }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;
  const isAdmin = can(currentOrganization, 'settings.integrations');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slim, setSlim] = useState(null);
  const slimDecided = useRef(false);
  const initialSlim = useRef(false);
  const [openStepId, setOpenStepId] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [error, setError] = useState(null);

  const fetchChecklist = useCallback(async () => {
    try {
      const response = await axios.get('/api/onboarding-checklist/');
      setData(response.data);
    } catch (err) {
      // A 403 here just means the role or organization cannot see the card.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    setSlim(null);
    setOpenStepId(null);
    slimDecided.current = false;
    fetchChecklist();
  }, [orgId, fetchChecklist]);

  const steps = data?.steps || [];
  const doneCount = steps.filter((step) => step.done).length;
  const nextStep = steps.find((step) => !step.done);
  const variant = data?.variant;
  const calm = variant === 'subscriber';

  // Slim is ephemeral per mount and decided ONCE, on the first payload: subscriber starts
  // slim, core once 3+ steps are done. Recomputing it on every refetch would collapse the
  // card under the user the moment an action ticks the third step off.
  if (variant && !slimDecided.current) {
    slimDecided.current = true;
    initialSlim.current = calm || doneCount >= SLIM_THRESHOLD;
  }

  const isSlim = slim === null ? initialSlim.current : slim;

  const handleHide = async () => {
    try {
      await axios.post('/api/onboarding-checklist/hide/', { hidden: true, variant });
      setData((previous) => (previous ? { ...previous, hidden: true } : previous));
    } catch (err) {
      setError(t('onboardingChecklist.hideError'));
    }
  };

  const goTo = (path) => {
    setOpenStepId(null);
    navigate(path);
  };

  const runCta = (stepId) => {
    const guide = STEP_GUIDES[stepId];
    if (!guide?.cta) return;
    if (guide.cta.action === 'createWorkOrder') {
      setOpenStepId(null);
      if (onCreateWorkOrder) onCreateWorkOrder();
    } else if (guide.cta.action === 'openInvoiceables') {
      setOpenStepId(null);
      if (onOpenInvoiceables) onOpenInvoiceables();
    } else if (guide.cta.action === 'navigate' && guide.cta.to) {
      goTo(guide.cta.to);
    }
  };

  const stepTitle = (stepId) => t(`onboardingChecklist.steps.${stepId}.title`);
  const stepDesc = (stepId) => t(`onboardingChecklist.steps.${stepId}.desc`);

  // Backend note payload -> i18n interpolation values, per step. `require` names the value
  // the done text cannot be rendered without; without it we fall back to a plain "Valmis".
  // Never name an interpolation value `count`: i18next treats it as a pluralization hint.
  const DONE_NOTE_SHAPES = {
    import: { require: 'customers', values: (n) => ({ customers: n.customers, vehicles: n.vehicles ?? 0 }) },
    workorder: { require: 'work_number', values: (n) => ({ workNumber: n.work_number }) },
    parts: { require: 'count', values: (n) => ({ parts: n.count }) },
    invoice: { require: 'invoice_number', values: (n) => ({ invoiceNumber: n.invoice_number }) },
    suppliers: { require: 'count', values: (n) => ({ suppliers: n.count }) },
    staff: { require: 'users', values: (n) => ({ users: n.users, pending: n.pending ?? 0 }) },
  };

  const doneNote = (step) => {
    const key = `onboardingChecklist.steps.${step.id}.doneNote`;
    const shape = DONE_NOTE_SHAPES[step.id];
    if (!shape) {
      // company / einvoice / terminal / booking: static done text, no note needed.
      return t(key, t('onboardingChecklist.doneFallback'));
    }
    const note = step.note || {};
    if (note[shape.require] === undefined || note[shape.require] === null || note[shape.require] === '') {
      return t('onboardingChecklist.doneFallback');
    }
    return t(key, shape.values(note));
  };

  const renderPanel = (stepId) => {
    if (stepId === 'import') {
      return (
        <ImportStepPanel
          autofuturRequestedAt={data?.autofutur_migration_requested_at}
          autofuturEmail={data?.autofutur_migration_request_email}
          onRequestMigration={() => setMigrationOpen(true)}
          onCsvImport={() => setCsvOpen(true)}
          onAddCustomer={() => setCustomerOpen(true)}
        />
      );
    }
    if (stepId === 'parts') {
      return (
        <PartsStepPanel
          openWorkOrders={data?.open_work_orders}
          onOpenWorkOrder={(id) => goTo(`/maintenances/${id}?addPart=1`)}
          onCreateWorkOrder={() => {
            setOpenStepId(null);
            if (onCreateWorkOrder) onCreateWorkOrder();
          }}
          onConnectSuppliers={() => goTo('/settings?tab=integrations')}
        />
      );
    }
    if (stepId === 'terminal') {
      return (
        <TerminalStepPanel
          requestedAt={data?.terminal_contact_requested_at}
          onRequested={(payload) => {
            setData((previous) =>
              previous
                ? { ...previous, terminal_contact_requested_at: payload?.requested_at || new Date().toISOString() }
                : previous
            );
          }}
        />
      );
    }

    const guide = STEP_GUIDES[stepId];
    if (!guide) return null;
    const adminOnly = ADMIN_ONLY_STEPS.has(stepId) && !isAdmin;
    return (
      <GuideStepPanel
        intro={t(`onboardingChecklist.steps.${stepId}.guide.intro`)}
        ways={guide.ways.map((way, index) => ({
          icon: way.icon,
          title: t(`onboardingChecklist.steps.${stepId}.guide.ways.${index}.title`),
          description: t(`onboardingChecklist.steps.${stepId}.guide.ways.${index}.desc`),
        }))}
        ctaLabel={t(`onboardingChecklist.steps.${stepId}.guide.cta`)}
        ctaWhere={t(`onboardingChecklist.steps.${stepId}.guide.ctaWhere`)}
        ctaIcon={guide.cta?.icon}
        onCta={() => runCta(stepId)}
        ctaDisabled={adminOnly}
        ctaHint={adminOnly ? t('onboardingChecklist.adminOnlyHint') : null}
        shot={guide.shot}
        shotAlt={stepTitle(stepId)}
      />
    );
  };

  const visible = Boolean(!loading && data && data.enabled && !data.hidden && variant && steps.length && doneCount !== steps.length);

  if (!visible) return null;

  const title = calm ? t('onboardingChecklist.subscriber.title') : t('onboardingChecklist.core.title');
  const subtitle = calm ? t('onboardingChecklist.subscriber.subtitle') : t('onboardingChecklist.core.subtitle');
  const progressLabel = t('onboardingChecklist.progress', { done: doneCount, total: steps.length });

  const dialogs = (
    <>
      <CSVImportDialog
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        onImportComplete={() => {
          fetchChecklist();
        }}
      />
      <CustomerDialog
        open={customerOpen}
        addCreatedCustomer={() => {
          setCustomerOpen(false);
          fetchChecklist();
        }}
        updateCustomer={() => {}}
        handleClose={() => setCustomerOpen(false)}
        customer={null}
      />
      <AutoFuturMigrationDialog
        open={migrationOpen}
        onClose={() => setMigrationOpen(false)}
        requestedAt={data.autofutur_migration_requested_at}
        requestedEmail={data.autofutur_migration_request_email}
        onRequested={(payload) => {
          setData((previous) =>
            previous
              ? {
                  ...previous,
                  autofutur_migration_requested_at: payload?.requested_at || new Date().toISOString(),
                  autofutur_migration_request_email: payload?.email || previous.autofutur_migration_request_email,
                }
              : previous
          );
        }}
      />
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );

  if (isSlim) {
    return (
      <Box data-testid="onboarding-checklist">
        <ChecklistSlimBar
          title={title}
          doneCount={doneCount}
          totalCount={steps.length}
          nextStepTitle={nextStep ? stepTitle(nextStep.id) : null}
          continueLabel={nextStep ? t('onboardingChecklist.continue', { step: stepTitle(nextStep.id) }) : ''}
          calm={calm}
          progressLabel={progressLabel}
          expandLabel={t('onboardingChecklist.showSteps')}
          hideLabel={t('onboardingChecklist.hide')}
          onExpand={() => setSlim(false)}
          onHide={handleHide}
        />
        {dialogs}
      </Box>
    );
  }

  const HeaderIcon = calm ? AutoAwesomeIcon : RocketLaunchIcon;

  return (
    <Box data-testid="onboarding-checklist">
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: calm ? 'divider' : 'rgba(49, 130, 206, 0.35)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: { xs: 2, sm: 2.75 }, pt: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
              <HeaderIcon sx={{ fontSize: 21, color: calm ? 'text.secondary' : 'secondary.main' }} />
              <Typography
                component="h2"
                sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-.2px' }}
              >
                {title}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '13.5px', lineHeight: 1.55, color: 'text.secondary', mt: 0.75, maxWidth: 620 }}>
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              endIcon={<ExpandLessIcon />}
              onClick={() => setSlim(true)}
              sx={{ textTransform: 'none' }}
              data-testid="onboarding-checklist-collapse"
            >
              {t('onboardingChecklist.collapse')}
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={handleHide}
              sx={{ textTransform: 'none', color: 'text.disabled' }}
              data-testid="onboarding-checklist-hide"
            >
              {t('onboardingChecklist.hide')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: { xs: 2, sm: 2.75 }, pt: 2, pb: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={steps.length ? (doneCount / steps.length) * 100 : 0}
            color="secondary"
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography sx={{ fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>{progressLabel}</Typography>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 2.75 }, pt: 1 }}>
          {steps.map((step, index) => {
            const state = step.done ? 'done' : step.id === nextStep?.id ? 'next' : 'todo';
            const expanded = openStepId === step.id;
            return (
              <ChecklistStepRow
                key={step.id}
                index={index}
                state={state}
                title={stepTitle(step.id)}
                description={stepDesc(step.id)}
                doneNote={doneNote(step)}
                expanded={expanded}
                onToggle={() => setOpenStepId(expanded ? null : step.id)}
                startLabel={t('onboardingChecklist.start')}
                closeLabel={t('onboardingChecklist.close')}
                data-testid={`onboarding-step-${step.id}`}
              >
                {expanded ? renderPanel(step.id) : null}
              </ChecklistStepRow>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.875,
            px: { xs: 2, sm: 2.75 },
            py: 1.75,
            mt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            fontSize: '12.5px',
            color: 'text.disabled',
          }}
        >
          <CheckSmallIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '12.5px', color: 'inherit' }}>
            {t('onboardingChecklist.autoCompleteHint')}
          </Typography>
        </Box>
      </Paper>
      {dialogs}
    </Box>
  );
}

export default OnboardingChecklistCard;
